import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { Pagination } from "../../domain/_kernel/Pagination.js"
import { OrderDirectionEnum } from "@antipartido-inc/contracts-ifaces"
import {
  NotificationPreferences,
  type PreferenceItem,
  type NotificationChannelKey,
} from "../../domain/notification/Models/NotificationPreferences.js"
import type { NotificationEventType } from "../../domain/notification/Models/NotificationEventType.js"
import type { Notification } from "../../domain/notification/Models/Notification.js"

const PAGE_SIZE = 20

interface NotificationState {
  unreadCount: number
  notifications: readonly Notification[]
  totalElements: number
  isLoadingList: boolean
  isLoadingMore: boolean
  preferences: NotificationPreferences | undefined
  isLoadingPreferences: boolean
}

interface NotificationActions {
  fetchUnreadCount: () => Promise<void>
  fetchNotifications: () => Promise<void>
  fetchMoreNotifications: () => Promise<void>
  fetchPreferences: () => Promise<void>
  updatePreference: (
    eventType: NotificationEventType,
    channel: NotificationChannelKey,
    newValue: boolean,
  ) => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<boolean>
  reset: () => void
}

type NotificationStore = NotificationState & NotificationActions

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    (set, get) => ({
      unreadCount: 0,
      notifications: [],
      totalElements: 0,
      isLoadingList: false,
      isLoadingMore: false,
      preferences: undefined,
      isLoadingPreferences: false,

      fetchUnreadCount: async () => {
        try {
          const { unreadCount } = await window.domain.GetUnreadCountNotificationUseCase.execute()
          set({ unreadCount: unreadCount.count })
        } catch (error) {
          console.warn("Failed to fetch unread count", error)
        }
      },

      fetchNotifications: async () => {
        try {
          set({ isLoadingList: true })
          const pagination = Pagination.create({
            offset: 0,
            limit: PAGE_SIZE,
            orderBy: "createdAt",
            orderDirection: OrderDirectionEnum.DESC,
          })
          const { notificationList } = await window.domain.GetNotificationsNotificationUseCase.execute({
            read: false,
            pagination,
          })
          set({
            notifications: notificationList.items,
            totalElements: notificationList.pagination.numElements ?? notificationList.items.length,
            isLoadingList: false,
          })
        } catch (error) {
          console.warn("Failed to fetch notifications", error)
          set({ isLoadingList: false })
        }
      },

      fetchMoreNotifications: async () => {
        const { notifications, totalElements } = get()
        try {
          set({ isLoadingMore: true })
          const pagination = Pagination.create({
            offset: notifications.length,
            limit: PAGE_SIZE,
            orderBy: "createdAt",
            orderDirection: OrderDirectionEnum.DESC,
          })
          const { notificationList } = await window.domain.GetNotificationsNotificationUseCase.execute({
            read: false,
            pagination,
          })
          set({
            notifications: [...notifications, ...notificationList.items],
            totalElements: notificationList.pagination.numElements ?? totalElements,
            isLoadingMore: false,
          })
        } catch (error) {
          console.warn("Failed to fetch more notifications", error)
          set({ isLoadingMore: false })
        }
      },

      fetchPreferences: async () => {
        try {
          set({ isLoadingPreferences: true })
          const { preferences } = await window.domain.GetNotificationPreferencesNotificationUseCase.execute()
          set({ preferences, isLoadingPreferences: false })
        } catch (error) {
          console.warn("Failed to fetch notification preferences", error)
          set({ isLoadingPreferences: false })
        }
      },

      updatePreference: async (
        eventType: NotificationEventType,
        channel: NotificationChannelKey,
        newValue: boolean,
      ) => {
        const { preferences } = get()
        if (!preferences) return

        const previous = preferences
        const existing = preferences.getByEventType(eventType)

        const updatedItems: readonly PreferenceItem[] = existing
          ? preferences.preferences.map((p) =>
              p.eventType === eventType ? { ...p, channels: { ...p.channels, [channel]: newValue } } : p,
            )
          : [
              ...preferences.preferences,
              {
                eventType,
                channels: {
                  inApp: channel === "inApp" ? newValue : true,
                  email: channel === "email" ? newValue : true,
                },
              },
            ]

        set({ preferences: NotificationPreferences.create({ preferences: [...updatedItems] }) })

        try {
          const { preferences: server } = await window.domain.UpdateNotificationPreferencesNotificationUseCase.execute({
            preferences: updatedItems,
          })
          set({ preferences: server })
        } catch (error) {
          console.warn("Failed to update notification preference", error)
          set({ preferences: previous })
        }
      },

      markAsRead: async (id: string) => {
        try {
          await window.domain.MarkNotificationReadNotificationUseCase.execute({ id })
          const { notifications } = get()
          set({ notifications: notifications.filter((n) => n.id !== id) })
          await get().fetchUnreadCount()
        } catch (error) {
          console.warn("Failed to mark notification as read", error)
        }
      },

      markAllAsRead: async () => {
        try {
          const { success } = await window.domain.MarkAllNotificationsReadNotificationUseCase.execute()
          if (success) {
            set({ unreadCount: 0, notifications: [], totalElements: 0 })
          }
          return success
        } catch (error) {
          console.warn("Failed to mark all notifications as read", error)
          return false
        }
      },

      reset: () => {
        set({
          unreadCount: 0,
          notifications: [],
          totalElements: 0,
          isLoadingList: false,
          isLoadingMore: false,
          preferences: undefined,
          isLoadingPreferences: false,
        })
      },
    }),
    { name: "NotificationStore" },
  ),
)
